import Api from '@/services/Api';
import { base, shopmeta } from '@/services/Endpoint';
import type { State } from './state';
import type { FontOptions, FontFace } from './types';
import HELPER from '@/app/utils/HELPER';
import { arrayClone, arrayFilter, arrayMap, arrayIndexFilter, insertElement } from '@/app/utils/nativeArray';
import { insertInstalledFontsWhenSavingFontManager } from '@/app/utils/manageInstalledFont';
import { renderFontList } from '@/app/editor/sidebar/utils/fontPicker';

type ActionArg = {
  state: State;
  rootState: any; // same as `store.state`, only in modules
  commit: Function; // same as `store.commit`
  dispatch: Function; // same as `store.dispatch`
  getters: any; // same as `store.getters`, or local getters if in modules
  rootGetters: any; // same as `store.getters`, only in modules
};

const formatThemeFontLocalData = (fonts: FontOptions[]) => {
  return arrayMap(fonts, (font) => {
    const fontWeight = font.fontWeight || '400';
    return `${font.styleName}:${fontWeight}`;
  }).sort((fontData1, fontData2) => {
    if (fontData1 > fontData2) return 1;
    if (fontData1 < fontData2) return -1;
    return 0;
  });
};

const formatIntalledFontStyleNames = (fonts: FontOptions[]) => {
  return arrayMap(fonts, (font) => font.styleName)
    .sort()
    .join(',');
};

const formatFontFaces = (faces?: FontFace[]) => {
  if (!faces) return '';
  return arrayMap(faces, (face) => `${face.fullname}-${face.style}-${face.weight}`)
    .sort()
    .join(',');
};

const actions = {
  async addFontOptions({ commit }: ActionArg, fonts: any) {
    const length = fonts.length;
    for (let i = 0; i < length; i++) {
      await commit('ADD_OPTIONS_FONTS', fonts[i]);
    }
  },
  async loadGoogleFonts({ state, dispatch }: ActionArg, payload?: { themeFonts: any }) {
    // async loadGoogleFonts({ state, dispatch }: ActionArg) {
    const res = (await Api.createXMLHttpRequest('GET', base.googleFontApi())) as { items: any[] };

    // Check font is existed Theme font
    await dispatch('loadThemeFonts', {
      onlyLocal: true,
      themeFonts: payload?.themeFonts,
    });
    const themeFonts = arrayClone(state.themeFonts);

    const googleOptionFonts = arrayFilter(state.optionFonts, (item) => {
      if (!item.custom) return true;
      const themeFontsLength = themeFonts.length;

      for (let i = 0; i < themeFontsLength; i++) {
        const font = themeFonts[i];
        if (font.styleName == item.styleName) {
          themeFonts.splice(i, 1);
          return false;
        }
      }

      return true;
    });

    const allGoogleFonts: FontOptions[] = arrayMap(res.items, (item) => {
      for (const font of googleOptionFonts) {
        if (font.styleName == item.family) {
          font.custom = false;
          return font;
        }
      }
      return {
        styleName: item.family,
        googleName: item.family,
        file: item.files.regular,
        isActive: false,
        custom: false,
      };
    });

    state.allGoogleFonts = allGoogleFonts;
  },
  async addFontOptionsAndLoadGoogleFonts(
    { state, commit, dispatch }: ActionArg,
    payload: { fonts: FontOptions[]; classifyOptionalFonts: boolean; needSave: boolean; fontMeta: any },
  ) {
    const { fonts, classifyOptionalFonts, needSave } = payload;

    await dispatch('addFontOptions', fonts);

    if (!state.installedFonts) {
      await dispatch('loadInstalledFont', { installedFonts: payload.fontMeta.installedFonts });
    }

    if (!state.allGoogleFonts.length) {
      await dispatch('loadGoogleFonts', { themeFonts: payload.fontMeta.themeFonts });

      // classify optional fonts
    } else if (classifyOptionalFonts) {
      const allGoogleFonts = state.allGoogleFonts;
      const optionFonts = state.optionFonts;

      // Check font is existed Theme font
      await dispatch('loadThemeFonts', {
        onlyLocal: true,
        themeFonts: payload.fontMeta.themeFonts,
      });
      const themeFonts = state.themeFonts;
      const themeFontsLength = themeFonts.length;

      const uncheckFontIndexes = arrayIndexFilter(optionFonts, (item) => {
        if (item.custom) {
          for (let j = 0; j < themeFontsLength; j++) {
            if (item.styleName == themeFonts[j].styleName) {
              return false;
            }
          }
        }

        return true;
      });

      // Check font is Install font
      if (state.installedFonts) {
        const installedFonts = state.installedFonts;
        const installedFontsLength = installedFonts.length;

        for (let i = 0; i < installedFontsLength; i++) {
          const uncheckFontsLength = uncheckFontIndexes.length;
          if (!uncheckFontsLength) break;

          const installedFont = installedFonts[i];

          for (let j = uncheckFontsLength - 1; j > -1; j--) {
            const index = uncheckFontIndexes[j];
            const optionalFont = optionFonts[index];

            if (installedFont.styleName === optionalFont.styleName) {
              installedFont.isActive = true;
              optionFonts[index] = installedFont;

              uncheckFontIndexes.splice(j, 0);
              break;
            }
          }
        }
      }

      if (!uncheckFontIndexes.length) return;

      // Check font is Google font
      const googleFontsLength = allGoogleFonts.length;
      for (let i = 0; i < googleFontsLength; i++) {
        const uncheckFontsLength = uncheckFontIndexes.length;
        if (!uncheckFontsLength) break;

        const googleFont = state.allGoogleFonts[i];

        for (let j = 0; j < uncheckFontsLength; j++) {
          const index = uncheckFontIndexes[j];
          const optionalFont = optionFonts[index];

          if (optionalFont.styleName == googleFont.styleName) {
            googleFont.isActive = true;
            optionFonts[index] = googleFont;
            uncheckFontIndexes.slice(j, 1);
            break;
          }
        }
      }
    }

    if (needSave) {
      await dispatch('saveFontOptions', false);
    } else {
      const optionalInstalledFonts = arrayFilter(state.optionFonts, (font) => font.isInstalled);
      insertInstalledFontsWhenSavingFontManager(optionalInstalledFonts);
    }
  },
  async saveFontOptions({ state, rootState, dispatch }: ActionArg, saveFromActive = true) {
    const activeFonts = saveFromActive ? arrayClone(state.activeFonts) : arrayClone(state.optionFonts);
    const localData = arrayMap(activeFonts, (font) => {
      const fontWeight = font.fontWeight || '400';
      return `${font.styleName}:${fontWeight}`;
    });

    const activeGoogleFonts = arrayFilter(
      activeFonts,
      (font) => !font.custom,
      (font) => font.googleName,
    );
    HELPER.addFontLink(activeGoogleFonts, {
      addMultipleFonts: true,
    });

    const optionalInstalledFonts = arrayFilter(activeFonts, (font) => font.isInstalled);
    insertInstalledFontsWhenSavingFontManager(optionalInstalledFonts);

    try {
      const payload = {
        key: 'active-font',
        value: JSON.stringify(localData),
      };
      await Api.base.post(shopmeta.base, payload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('saveActiveFontListError', error);
    }

    if (state.changeInstalledFonts) {
      state.installedFonts = arrayMap(state.newInstalledFonts, (font) => {
        font.fontFaces = font.newFontFaces;
        font.newFontFaces = [];
        return font;
      });
      await dispatch('editInstalledFont');
    }

    await dispatch('saveThemeFonts');

    state.optionFonts = activeFonts;

    if (state.setupFontSelector.size) {
      const allSetups = state.setupFontSelector.values();

      for (const item of allSetups) {
        const $element = jQuery(`#${item.id}`);
        const data = { settings: item.settings, $element };
        await renderFontList(false, data);
      }
    }
  },
  checkChangeThemeFonts({ state, rootState }: ActionArg) {
    const shopDomain = rootState.app.shopInfo.shopifyDomain;
    const localDataString = localStorage.getItem(`gfThemeFontList_${shopDomain}`) || '[]';
    const currentData = formatThemeFontLocalData(state.themeFonts);
    const currentDataString = JSON.stringify(currentData);
    state.changeThemeFonts = localDataString != currentDataString;
  },
  checkChangeInstalledFonts({ state }: ActionArg) {
    const newInstalledFonts = state.newInstalledFonts;
    const installedFonts = state.installedFonts || [];

    const changeFontFaces = !installedFonts.every((font) => {
      const oldFontFaces = formatFontFaces(font.fontFaces);
      const newFontFaces = formatFontFaces(font.newFontFaces);

      return newFontFaces == oldFontFaces;
    });

    const newInstalledFontNames = formatIntalledFontStyleNames(newInstalledFonts);
    const oldInstalledFontNames = formatIntalledFontStyleNames(installedFonts);

    const changeStyleNames = newInstalledFontNames != oldInstalledFontNames;

    state.changeInstalledFonts = changeFontFaces || changeStyleNames;
  },
  addThemeFont({ state, dispatch }: ActionArg, payload: any) {
    const existedThemeFont = state.themeFonts.find(
      (item) => item.styleName.toLowerCase() === payload.styleName.toLowerCase(),
    );
    if (!existedThemeFont) {
      state.themeFonts.push(payload);
      dispatch('checkChangeThemeFonts');
      return false;
    }
    return true;
  },
  removeThemeFont({ state, dispatch }: ActionArg, font: FontOptions) {
    const removedThemeIndex = state.themeFonts.findIndex((item) => item.styleName == font.styleName);
    if (removedThemeIndex > -1) {
      state.themeFonts.splice(removedThemeIndex, 1);

      const removedActiceIndex = state.activeFonts.findIndex((item) => item.styleName == font.styleName);
      if (removedActiceIndex > -1) {
        state.activeFonts.splice(removedActiceIndex, 1);
      }

      dispatch('checkChangeThemeFonts');
    }
  },
  async saveThemeFonts({ state, rootState }: ActionArg) {
    const localData = formatThemeFontLocalData(state.themeFonts);

    try {
      const payload = {
        key: 'theme-font',
        value: JSON.stringify(localData),
      };
      await Api.base.post(shopmeta.base, payload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('saveThemeFontError', error);
    }
  },
  openFontManager({ state, dispatch, rootState }: ActionArg, isShown: boolean) {
    state.showFontManager = isShown ? 1 : 0;
    if (isShown) {
      const cloneOptionalFonts = arrayClone(state.optionFonts);
      state.activeFonts = cloneOptionalFonts;

      if (state.installedFonts) {
        const cloneInstalledFonts = arrayMap(state.installedFonts, (font) => {
          if (font.fontFaces) {
            font.newFontFaces = arrayClone(font.fontFaces, true);
          }
          return font;
        });
        state.newInstalledFonts = cloneInstalledFonts;
      }

      dispatch('loadThemeFonts', { themeFonts: rootState.app.fontMeta.themeFonts });
    } else {
      const optionFonts = arrayMap(state.optionFonts, (item) => {
        item.isActive = true;
        return item;
      });

      const activeFonts = state.activeFonts;
      const activeFontsLength = activeFonts.length;

      for (let i = activeFontsLength - 1; i >= 0; i--) {
        const font = activeFonts[i];
        const founded = optionFonts.find((item) => {
          return item.styleName === font.styleName && item.custom === font.custom;
        });

        if (!founded) {
          font.isActive = false;
        }
      }

      state.optionFonts = optionFonts;
      state.activeFonts = [];

      if (state.installedFonts) {
        state.installedFonts = arrayMap(state.installedFonts, (font) => {
          font.newFontFaces = [];
          return font;
        });
      }
      state.newInstalledFonts = [];
      state.changeInstalledFonts = false;
      state.editingFonts = [];
      state.uploadStatus = '';

      state.themeFonts = [];
      state.changeThemeFonts = false;
    }
  },
  async loadThemeFonts({ state, rootState }: ActionArg, payload?: { onlyLocal: boolean; themeFonts: any }) {
    try {
      const valueString = payload?.themeFonts || '[]';
      const themeFontList = JSON.parse(valueString) as string[];
      const onlyLocal = payload?.onlyLocal;
      const shopDomain = rootState.app.shopInfo.shopifyDomain;
      localStorage.setItem(`gfThemeFontList_${shopDomain}`, valueString);
      const optionalCustomFonts = onlyLocal
        ? []
        : arrayFilter(state.optionFonts, (font) => font.custom && !font.isInstalled);
      const themeFonts = arrayMap(themeFontList, (item) => {
        const [styleName, fontWeight] = item.split(':');
        const optionFontsLength = optionalCustomFonts.length;
        if (optionFontsLength) {
          for (let i = 0; i < optionFontsLength; i++) {
            const font = optionalCustomFonts[i];
            if (font.styleName == styleName) {
              optionalCustomFonts.splice(i, 1);
              return font;
            }
          }
        }

        return {
          styleName: styleName,
          googleName: styleName,
          isActive: false,
          custom: true,
          fontWeight,
        };
      });
      state.themeFonts = themeFonts.concat(optionalCustomFonts);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('getThemeFontError', error);
    }
  },
  async startCreateInstalledFont({ state, dispatch }: ActionArg, newFont: FontOptions) {
    if (state.installedFonts && newFont.fontFaces) {
      newFont.newFontFaces = arrayClone(newFont.fontFaces, true);
      insertElement(state.installedFonts, newFont);
      insertElement(state.newInstalledFonts, newFont);
    } else {
      state.newInstalledFonts = state.installedFonts = [newFont];
    }
    state.editingFonts.push(newFont);
    await dispatch('editInstalledFont');
  },
  async addFontFaceToExistedFamily({ state, dispatch }: ActionArg, payload: any) {
    const { fontFace, targetFontFamily } = payload;
    const installedFonts = state.newInstalledFonts;
    const length = installedFonts.length;
    for (let i = 0; i < length; i++) {
      if (installedFonts[i].styleName.toLowerCase() === targetFontFamily.toLowerCase()) {
        const { fontFaces, newFontFaces } = installedFonts[i];
        fontFaces?.push(fontFace);
        newFontFaces?.push(fontFace);
      }
    }
    await dispatch('editInstalledFont');
  },
  async loadInstalledFont({ state }: ActionArg, payload: { installedFonts: any }) {
    let installedFonts = [];
    try {
      const valueString = payload?.installedFonts || [];
      const value = JSON.parse(valueString);
      installedFonts = arrayMap(value, (item: any) => {
        return Object.assign(item, {
          googleName: item.styleName,
          isActive: false,
          isInstalled: true,
          custom: true,
        });
      });
    } catch (error) {
      // eslint-disable-next-line no-console
    }

    const optionalFonts = arrayClone(state.optionFonts);
    const installedFontsLength = installedFonts.length;

    for (let i = 0; i < installedFontsLength; i++) {
      const installedFont = installedFonts[i];

      const optionalFontsLength = optionalFonts.length;
      for (let j = optionalFontsLength - 1; j > -1; j--) {
        const optionalFont = optionalFonts[j];

        if (installedFont.styleName === optionalFont.styleName) {
          optionalFont.isInstalled = true;
          optionalFont.fontFaces = installedFont.fontFaces;
          installedFonts[i] = optionalFont;

          optionalFonts.splice(j, 0);
          break;
        }
      }
    }

    state.installedFonts = installedFonts;
  },
  async editInstalledFont({ state }: ActionArg) {
    try {
      if (!state.installedFonts) return;
      const installedFonts = arrayMap(state.installedFonts, (font) => {
        return {
          styleName: font.styleName,
          fontFaces: font.fontFaces,
        };
      });
      const payload = {
        key: 'installed-font',
        value: JSON.stringify(installedFonts),
      };
      await Api.base.post(shopmeta.base, payload);
    } catch (error) {}
  },
  async readFiles({ state, commit, dispatch }: ActionArg, files: any[]) {
    const filesLength = files.length;
    commit('SET_UPLOAD_NUMBER', filesLength);

    const fontFileTypes = state.fontFileTypes;

    try {
      commit('SET_UPLOAD_STATUS', 'loading');
      if (filesLength) {
        const fileResources = arrayMap(files, (file) => {
          const fullname = file.name;
          const [name, type] = fullname.split('.');

          if (!fontFileTypes.includes(type)) {
            throw new Error('wrong file type');
          }

          return {
            fullname,
            name,
            type,
            size: file.size,
            file,
          };
        });

        const parametersList = await dispatch('uploadFileProcess', fileResources);
        const fileResourcesLength = fileResources.length;

        const listLength = parametersList.length;
        for (let i = 0; i < listLength; i++) {
          const parameters = parametersList[i];
          const formData = new FormData();

          let key = null;
          const paramLength = parameters.length;
          for (let j = 0; j < paramLength; j++) {
            const param = parameters[j];
            formData.set(param.name, param.value);

            if (param.name == 'key') {
              key = param.value;
            }
          }

          for (let x = 0; x < fileResourcesLength; x++) {
            const resource = fileResources[x];
            const fullname = resource.fullname.replaceAll(' ', '_');
            if (key.includes(fullname)) {
              resource.key = key;

              formData.append('file', resource.file);
            }
          }

          await dispatch('uploadFile', formData);
        }

        await dispatch('createFilesGraphQL', fileResources);

        const getFontFilesResponse = await Api.excuteGraphQL('FilesQuery', {
          first: filesLength,
          sortKey: 'CREATED_AT',
          reverse: true,
        });

        const fontsFiles = getFontFilesResponse.data.data.data.files.edges;

        if (fontsFiles.length) {
          for (const item of fontsFiles) {
            for (const resource of fileResources) {
              const url = item.node.url;
              const resourceName = resource.name.replaceAll(' ', '_');
              resource.name = resourceName;
              if (url && url.includes(resource.name) && url.includes(resource.type)) {
                resource.url = url;
              }
            }
          }
        } else {
          throw new Error("Can't read data");
        }

        for (const item of fileResources) {
          const installedFontName = await dispatch('checkNewInstalledFontName', item.name);
          if (!installedFontName) {
            throw new Error('Duplicate name');
          }
          const canAddToFontFamily = await dispatch('checkFontFamily', item.name);
          if (!canAddToFontFamily) {
            const fontFaces = [
              {
                fullname: item.fullname,
                weight: '400',
                style: 'normal',
                url: item.url,
              },
            ];
            const newFont = {
              styleName: installedFontName,
              googleName: installedFontName,
              isActive: false,
              custom: false,
              isInstalled: true,
              fontFaces,
            };
            await dispatch('startCreateInstalledFont', newFont);
          } else {
            const payload = {
              fontFace: {
                fullname: item.fullname,
                weight: '400',
                style: 'normal',
                url: item.url,
              },
              targetFontFamily: canAddToFontFamily.familyName,
            };
            await dispatch('addFontFaceToExistedFamily', payload);
          }
        }
      }
      commit('SET_UPLOAD_STATUS', 'success');
      commit('SET_UPLOAD_NUMBER', 0);
    } catch (error) {
      commit('SET_UPLOAD_STATUS', 'error');
      commit('SET_UPLOAD_NUMBER', 0);
    }
  },
  checkNewInstalledFontName({ state }: ActionArg, inputName: string) {
    const fontName = inputName.split('-')[0];
    if (state.newInstalledFonts) {
      const allFonts = state.allGoogleFonts.concat(state.themeFonts);
      const allFontsLength = allFonts.length;
      for (let i = 0; i < allFontsLength; i++) {
        if (fontName.toLowerCase() == allFonts[i].styleName.toLowerCase()) {
          return false;
        }
      }
    }

    return fontName;
  },
  checkDuplicateFontFaceInFamily(args: ActionArg, payload: any) {
    const { fontName, newFontFaces } = payload;
    const uploadedFontFaces: string[] = [];
    const length = newFontFaces.length;

    for (let i = 0; i < length; i++) {
      const fontFace = newFontFaces[i];
      const fontFaceName = fontFace.fullname.split('.')[0];
      uploadedFontFaces.push(fontFaceName.toLowerCase());
    }

    return uploadedFontFaces.includes(fontName.toLowerCase());
  },
  async checkFontFamily({ state, dispatch }: ActionArg, inputName: string) {
    const fontName = inputName.split('-')[0];
    if (state.newInstalledFonts) {
      const allFonts = state.newInstalledFonts;
      const length = allFonts.length;

      for (let i = 0; i < length; i++) {
        if (fontName.toLowerCase() === allFonts[i].styleName.toLowerCase()) {
          const payload = {
            fontName: inputName,
            newFontFaces: allFonts[i].newFontFaces,
          };
          const isExistedFontFace = await dispatch('checkDuplicateFontFaceInFamily', payload);
          if (isExistedFontFace) throw new Error('Duplicate font face');
          return { familyName: allFonts[i].styleName.toLowerCase() };
        }
      }
      return false;
    }
  },
  async uploadFileProcess(args: ActionArg, fileResources: any[]) {
    const input = arrayMap(fileResources, (resource) => ({
      mimeType: `font/${resource.type}`,
      filename: resource.fullname,
      fileSize: resource.size + '', //convert to string
      resource: 'FILE',
      httpMethod: 'POST',
    }));

    const stagedUploadsReponse = await Api.excuteGraphQL('StagedUploadsCreate', {
      input,
    });

    const stagedUploadsData = stagedUploadsReponse.data.data.data;

    const stagedTargets = stagedUploadsData.stagedUploadsCreate.stagedTargets as any[];
    return arrayMap(stagedTargets, (item) => item.parameters);
  },
  async uploadFile(args: ActionArg, formData: FormData) {
    await Api.createXMLHttpRequest('POST', base.shopifyS3, formData);

    return false;
  },
  async createFilesGraphQL(args: ActionArg, fileResources: any[]) {
    const input = arrayMap(fileResources, (resource) => ({
      originalSource: `https://shopify.s3.amazonaws.com/${resource.key}`,
      contentType: 'FILE',
    }));

    await Api.excuteGraphQL('FileCreate', {
      input,
    });
  },
  async updateFontFace(
    { state, dispatch }: ActionArg,
    { styleName, index, attr, value }: { styleName: string; index: number; attr: 'weight' | 'style'; value: string },
  ) {
    const fontIndex = state.newInstalledFonts.findIndex((font) => font.styleName === styleName);
    if (fontIndex > -1) {
      const font = state.newInstalledFonts[fontIndex];
      if (font.newFontFaces) {
        const fontFace = font.newFontFaces[index];
        fontFace[attr] = value;
      }

      state.newInstalledFonts[fontIndex] = font;
      await dispatch('checkChangeInstalledFonts');
    }
  },
  async deleteFontFace(
    { state, commit, dispatch }: ActionArg,
    { styleName, index }: { styleName: string; index: number },
  ) {
    const fontIndex = state.newInstalledFonts.findIndex((font) => font.styleName === styleName);
    let needDeleteFont = false;

    if (fontIndex > -1) {
      const font = state.newInstalledFonts[fontIndex];
      if (font.newFontFaces) {
        font.newFontFaces.splice(index, 1);

        if (!font.newFontFaces.length) {
          needDeleteFont = true;
        }
      }

      if (needDeleteFont) {
        if (font.isActive) commit('REMOVE_ACTIVE_FONTS', font);
        state.newInstalledFonts.splice(fontIndex, 1);
      } else {
        state.newInstalledFonts[fontIndex] = font;
      }
      await dispatch('checkChangeInstalledFonts');
    }
  },
};

export default actions;
